import { TemplateDefaults } from "../../TemplateDefaults";
import { Box2, Line, Vector2 } from "three";
import { DOMHitTest } from "../../../library-js/dom-tools/DOMHitTest";
import { Events, OnDrag, OnKeyDown, OnKeyUp, OnMouseDown, OnMouseEnter, OnMouseLeave, OnMouseMove, OnMouseUp } from "../../../library-js/dom-tools/EventListeners";
import { DragData } from "../../../library-js/dom-ui/basics/DragData";
import { NodeGraphView } from "../NodeGraphView";
import { NodeGraphNode as NodeGraphNode } from "./NodeGraphNode";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { NodeGraphEdge } from "./NodeGraphEdge";
import { IncrementalIDGenerator } from "../../../library-js/random/IncrementalIDGenerator";
import { OnAnimationFrame } from "../../../library-js/animation/OnAnimationFrame";
import { NGEdgeCreator } from "../interactions/NGEdgeCreator";
import { NodeGraphEvent } from "../interactions/NodeGraphEvent";
import { NodeGraphInteraction } from "../interactions/NodeGraphInteraction";
import { NodeGraphComponent } from "./NodeGraphComponent";
import { NodeGraphReference } from "./NodeGraphReference";
import { NodeGraphController } from "../interactions/NodeGraphController";
import { NodeGraphUI } from "../ui/NodeGraphUI";
import { NodeGraphSelection } from "./NodeGraphSelection";
import { Line2 } from "../../../library-js/geometry/Line2";
import { NGCommandManager } from "../commands/NGCommandManager";
import { Arrays } from "../../../library-js/tools/Arrays";
import { NGCommandContext } from "../commands/NGCommandContext";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { Box2Extensions } from "../../../library-js/geometry/Box2Extensions";
import { Intersects2 } from "../../../library-js/geometry/Intersects2";

export class NodeGraph
{
  _nodes:NodeGraphNode[] = [];
  _edges:NodeGraphEdge[] = [];

  _idGenerator = new IncrementalIDGenerator();

  _references:Map<string,NodeGraphReference> = new Map<string,NodeGraphReference>();
  _referenced:Map<NodeGraphReference, string> = new Map<NodeGraphReference, string>();

  _onAnimationFrame = new OnAnimationFrame();  
  
  _controller:NodeGraphController = new NodeGraphController( this );  
  _ui:NodeGraphUI = new NodeGraphUI( this );
  _selection:NodeGraphSelection;
  _commandManager:NGCommandManager;
  _commandContext:NGCommandContext;
  _hasKeyboardFocus:boolean = true;

  initialize( e:Element )
  {
    this._selection = new NodeGraphSelection( this );
    
    this._commandManager = new NGCommandManager( this );
    this._commandContext = new NGCommandContext();

    this._commandManager.updateKeyboardCommands();

    this._ui.initialize( e );
    this._controller._initialize();

    this._createUpdater();        

    this._initializeNodes();  

    this._ui.updateEdges();


    OnMouseDown.add( this._ui.element as HTMLElement, 
      ( me )=>
      {
        me.preventDefault();
        
      }  
    );
  
    OnKeyDown.add( document.documentElement, 
      ( ke )=>
      {
        if ( ! this._hasKeyboardFocus )
        {
          return;
        }

        console.log( ke );
        this._controller._event.altKey.setNext( ke.altKey );
        this._controller._event.ctrlKey.setNext( ke.ctrlKey );
        this._controller._event.shiftKey.setNext( ke.shiftKey );
        this._controller._event.setKey( ke.key, true );

        this._commandManager.processKeyboardInput( ke, this._commandContext ); 
      }
    );

    OnKeyUp.add( document.documentElement, 
      ( ke )=>
      {
        if ( ! this._hasKeyboardFocus )
        {
          return;
        }
        
        console.log( ke );
        this._controller._event.altKey.setNext( ke.altKey );
        this._controller._event.ctrlKey.setNext( ke.ctrlKey );
        this._controller._event.shiftKey.setNext( ke.shiftKey );
        this._controller._event.setKey( ke.key, false );

        this._commandManager.processKeyboardInput( ke, this._commandContext ); 
      }
    )

  }   

  update()
  {
    this._controller._nodeGraphEvent.bounds = DOMHitTest.getPageBox( this._ui.element ); 
    this._controller.update();    


    this._ui.update();
  }
 
  _createUpdater()
  {
    this._onAnimationFrame.addListener( ()=> this.update() );
    this._onAnimationFrame.run();    
  }


  _initializeNodes()
  {
    let nodeElements = NodeGraphNode.type.queryAll( this._ui.element );
    nodeElements.forEach(  el => this._addNode( el ) );
  }

  _addNode( nodeElement:Element )
  {
    let ngNode = new NodeGraphNode();
    ngNode.intizialize( this, nodeElement );
    
    this._nodes.push( ngNode );

  }

  _addEdge( from:NodeGraphComponent, to:NodeGraphComponent )
  {
    let edge = new NodeGraphEdge();
    edge._fromConnector = from;
    edge._toConnector = to;

    edge.setID( this );
    this._edges.push( edge );

    console.log( "Created edge:", edge );
  }

  getClosestEdge( p:Vector2, distance:number = 25 ):NodeGraphEdge
  {
    let closest = -1;
  
   
    for ( let i = 0; i < this._edges.length; i++ )
    {
      let edgeDistance = this._edges[ i ].getDistance( p );

      if ( edgeDistance < distance )
      {
        distance = edgeDistance;
        closest = i;
      }
    } 

    return closest == -1 ? null : this._edges[ closest ];
  }

  getEdgesInsideBox( box:Box2 )
  {
    let edges:NodeGraphEdge[] = [];

    let lines = Box2Extensions.getLines( box );

    let edgeLine = new Line2( new Vector2(0,0), new Vector2(0,0) );

    for ( let i = 0; i < this._edges.length; i++ )
    {
      edgeLine.start.copy( this._edges[ i ].start );
      edgeLine.end.copy( this._edges[ i ].end );

      if ( Intersects2.Box2Line2( box, edgeLine, lines ) )
      {
        edges.push( this._edges[ i ] );
      }  
    }

    return edges;

  }

  getNodesInsideBox( box:Box2 )
  {
    let nodes:NodeGraphNode[] = [];

    box = box.clone().translate( this._controller._event.pageOffset );

    for ( let i = 0; i < this._nodes.length; i++ )
    {
      let nodeBox = DOMHitTest.getPageBox( this._nodes[ i ]._htmlElement );

      if ( box.intersectsBox( nodeBox ) )
      {
        nodes.push( this._nodes[ i ] );
      }  
    }

    return nodes;
  }

  deleteNode( node:NodeGraphNode )
  {
    let idSet = new Set<string>( node._components.map( c => c._id ) );
    

    let removals:NodeGraphEdge[] = [];
    
    for ( let i = 0; i < this._edges.length; i++ )
    {
      let e = this._edges[ i ];

      if ( idSet.has( e._fromConnector._id ) || idSet.has( e._toConnector._id ) )
      {
        removals.push( e );
      }
    }

    removals.forEach( r => this.deleteEdge( r ) );


    this._deleteReference( node );
    Arrays.remove( this._nodes, node );
    
    HTMLEditor.remove( node._htmlElement );
  }

  deleteEdge( ref:NodeGraphEdge )
  {
    this._deleteReference( ref );

    Arrays.remove( this._edges, ref );
  }

  _deleteReference( reference:NodeGraphReference )
  {
    this._references.delete( reference._id );
    this._referenced.delete( reference );

    reference._id = null;
  }

  get( id:string ):NodeGraphReference
  {
    if ( ! this._references.has( id ) )
    {
      return null;
    }

    return this._references.get( id );
  }

  

  _ensureID( nodeGraphReference:NodeGraphReference ):string
  {
    if ( this._referenced.has( nodeGraphReference ) )
    {
      return this._referenced.get( nodeGraphReference );
    }

    let id = this._idGenerator.createID();

    this._referenced.set( nodeGraphReference, id );
    this._references.set( id, nodeGraphReference );

    console.log( "Adding:", id, nodeGraphReference );
    return id;
    
  }


}